import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { PortalApiService } from './portal-api.service';
import { PortalBrandingService } from './portal-branding.service';
import { PortalPreviewService } from './portal-preview.service';

export interface PortalUser {
	id: number;
	name: string;
	email: string;
	first_name?: string;
	last_name?: string;
	phone_dial_code?: string;
	phone?: string;
}

@Injectable({ providedIn: 'root' })
export class PortalAuthService {

	user: PortalUser | null = null;
	// True when the current host isn't a registered portal (unknown subdomain).
	unknownPortal = false;

	private sessionCheck: Promise<PortalUser | null> | null = null;

	constructor(
		private api: PortalApiService,
		private branding: PortalBrandingService,
		private preview: PortalPreviewService,
		private router: Router
	) {}

	ensureSession(force = false): Promise<PortalUser | null> {
		if (this.user && !force) return Promise.resolve(this.user);
		if (this.sessionCheck && !force) return this.sessionCheck;

		this.sessionCheck = new Promise<PortalUser | null>(resolve => {
			this.api.auth.session((res: any) => {
				if (res?.unknown_portal) {
					this.unknownPortal = true;
					this.user = null;
					resolve(null);
					return;
				}
				this.unknownPortal = false;
				this.branding.apply(res?.branding);
				this.maybeRedirectAlias(res?.branding);
				this.user = res?.user || null;
				resolve(this.user);
			}, () => {
				this.user = null;
				resolve(null);
			});
		});
		return this.sessionCheck;
	}

	private maybeRedirectAlias(branding: any): void {
		const org = branding?.organisation;
		const current = org?.alias;
		const matched = org?.alias_matched;
		if (!current || !matched || current === matched) return;

		// Only redirect when the browser is genuinely on the old subdomain — skips
		// dev on localhost, where the alias comes from the X-Portal-Host header.
		const host = window.location.host;
		if (!host.startsWith(matched + '.')) return;

		const newHost = current + host.slice(matched.length);
		window.location.replace(
			window.location.protocol + '//' + newHost + window.location.pathname + window.location.search + window.location.hash
		);
	}

	setUser(user: PortalUser | null): void {
		this.user = user;
		this.sessionCheck = null;
	}

	loginUrlTree(returnUrl?: string | null): UrlTree {
		const redirect = this.safeReturnUrl(returnUrl === undefined ? this.router.url : returnUrl);
		return this.router.createUrlTree(['/login'], redirect ? { queryParams: { redirect } } : {});
	}

	goToLogin(returnUrl?: string | null): void {
		// A preview has no account to sign into. Sending it to the login form would strand staff on a
		// screen they can't get past, so the expired-preview card is left showing instead.
		if (this.preview.active) return;

		const target = this.loginUrlTree(returnUrl);
		this.setUser(null);
		this.router.navigateByUrl(target);
	}

	safeReturnUrl(url: string | null | undefined): string | null {
		if (!url || !url.startsWith('/') || url.startsWith('//')) return null;

		const path = url.split('?')[0].split('#')[0];
		if (path === '/' || path === '/login' || path === '/forgot-password') return null;
		if (path.startsWith('/reset-password') || path.startsWith('/accept-invite')) return null;

		return url;
	}

	logout(): void {
		// A deliberate sign-out, so no return URL — they go back to a clean login page.
		const done = () => {
			this.setUser(null);
			this.router.navigate(['/login']);
		};
		this.api.auth.logout(done, done);
	}
}
